import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Building2, Radar, Users, Network, ShieldCheck, ScrollText, UserPlus, Settings,
  Menu, X, Circle, RefreshCw, LogOut, ChevronDown, Shield, Inbox, GraduationCap, BookOpen, KeyRound, PhoneCall, Contact,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ApprovalPush } from "@/components/ApprovalPush";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";
import { gmail } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const NAV = [
  { to: "/hq", label: "Digital Headquarters", icon: Building2, testid: "nav-hq" },
  { to: "/mission", label: "Mission Control", icon: Radar, testid: "nav-mission" },
  { to: "/employees", label: "My Associates", icon: Users, testid: "nav-employees" },
  { to: "/support", label: "Support Inbox", icon: Inbox, testid: "nav-support" },
  { to: "/calls", label: "Call Log", icon: PhoneCall, testid: "nav-calls" },
  { to: "/contacts", label: "Contacts", icon: Contact, testid: "nav-contacts" },
  { to: "/departments", label: "Departments", icon: Network, testid: "nav-departments" },
  { to: "/associate-training", label: "Associate Training", icon: GraduationCap, testid: "nav-training", adminOnly: true },
  { to: "/about", label: "About ABC Services", icon: BookOpen, testid: "nav-about", adminOnly: true },
  { to: "/approvals", label: "Approval Center", icon: ShieldCheck, testid: "nav-approvals" },
  { to: "/activity", label: "Activity", icon: ScrollText, testid: "nav-activity" },
  { to: "/hire", label: "Add Associates", icon: UserPlus, testid: "nav-hire" },
  { to: "/logins", label: "Add Logins", icon: KeyRound, testid: "nav-logins", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

function NavItems({ onNavigate, pendingApprovals, supportUnread, isAdmin }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Primary">
      {NAV.filter((item) => !item.adminOnly || isAdmin).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={item.testid}
          className={({ isActive }) =>
            cn(
              "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-200",
              isActive
                ? "bg-[#0A1326] text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span className="absolute left-0 top-1/2 h-6 -translate-y-1/2 rounded-full" style={{ width: 3, background: "#00E5FF", boxShadow: "0 0 10px #00E5FF" }} />
              )}
              <item.icon size={18} className={cn("shrink-0", isActive && "text-[#00E5FF]")} />
              <span className="font-medium">{item.label}</span>
              {item.to === "/approvals" && pendingApprovals > 0 && (
                <span className="ml-auto rounded-full bg-[#FFBF00]/15 px-2 py-0.5 text-[10px] font-mono text-[#FFBF00]" data-testid="nav-approvals-badge">
                  {pendingApprovals}
                </span>
              )}
              {item.to === "/support" && supportUnread > 0 && (
                <span className="ml-auto rounded-full bg-[#00E5FF]/15 px-2 py-0.5 text-[10px] font-mono text-[#00E5FF]" data-testid="nav-support-badge">
                  {supportUnread > 49 ? "49+" : supportUnread}
                </span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { state, reset } = useStore();
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const online = state.employees.filter((e) => e.status !== "off_duty").length;
  const pending = state.approvals.filter((a) => a.status === "pending").length;
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => { if (!user) refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the support inbox unread count so the nav badge stays fresh.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try { const r = await gmail.unreadCount(); if (!cancelled) setSupportUnread(r.connected ? r.unread : 0); }
      catch (_) {}
    };
    tick();
    const iv = setInterval(tick, 45000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [location.pathname]);

  const signOut = async () => {
    try { await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }); } catch (_) {}
    try { localStorage.removeItem("iq-guest"); } catch (_) {}
    toast.success("Signed out");
    navigate("/");
  };

  const displayName = user?.name || (user?.guest ? "Guest" : "Account");
  const role = user?.role || (user?.guest ? "viewer" : "viewer");
  const initial = (displayName || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="relative min-h-screen bg-[#030A16] bg-radials grain">
      <ApprovalPush />
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-white/5 bg-[#050D1C]/80 backdrop-blur-xl lg:flex">
        <div className="flex items-center px-5 py-5">
          <Logo size={38} withWordmark subtitle="IQ IA Solutions" />
        </div>
        <div className="px-3">
          <NavItems pendingApprovals={pending} supportUnread={supportUnread} isAdmin={role === "admin"} />
        </div>
        <div className="mt-auto p-4">
          <div className="glass rounded-xl p-3">
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <Circle size={9} className="fill-emerald-400 text-emerald-400" />
              <span className="font-mono">{online} associates online</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">ABC Services HQ · demo</div>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/5 bg-[#030A16]/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Logo size={32} withWordmark />
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-lg border border-white/10 p-2 text-slate-300"
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu size={20} />
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 border-r border-white/10 bg-[#050D1C] p-4">
            <div className="mb-4 flex items-center justify-between">
              <Logo size={34} withWordmark />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="text-slate-400">
                <X size={20} />
              </button>
            </div>
            <NavItems pendingApprovals={pending} supportUnread={supportUnread} isAdmin={role === "admin"} onNavigate={() => setMobileOpen(false)} />
            <button onClick={() => { setMobileOpen(false); signOut(); }} className="mt-3 flex w-full items-center gap-2 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-slate-300 hover:border-[#FF0055]/40 hover:text-white" data-testid="signout-btn-mobile">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Desktop top bar */}
        <div className="sticky top-0 z-20 hidden items-center justify-between border-b border-white/5 bg-[#030A16]/70 px-8 py-3.5 backdrop-blur-xl lg:flex">
          <div className="text-sm text-slate-400">
            <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-mono uppercase text-slate-500">Demo data</span>
          </div>
          <div className="flex items-center gap-4">
            {state.syncing && (
              <span className="flex items-center gap-1.5 text-xs text-[#00E5FF]" data-testid="sync-indicator">
                <RefreshCw size={13} className="animate-spin" /> Saving
              </span>
            )}
            <span className="flex items-center gap-2 text-sm text-slate-300">
              <Circle size={9} className="fill-emerald-400 text-emerald-400" />
              <span className="font-mono">{online} online</span>
            </span>
            <button
              onClick={() => { reset(); toast.success("Demo reset", { description: "All approvals, hires and training restored to the seeded state." }); }}
              className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-[#00E5FF]/40 hover:text-white"
              data-testid="reset-demo-btn"
            >
              Reset demo
            </button>

            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} data-testid="header-profile" className="flex items-center gap-2 rounded-full border border-white/10 py-1 pl-1 pr-2.5 transition-colors hover:border-[#00E5FF]/40">
                {user?.picture ? (
                  <img src={user.picture} alt={displayName} className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] text-xs font-semibold text-[#030A16]">{initial}</span>
                )}
                <span className="max-w-[130px] truncate text-xs text-slate-200" data-testid="header-username">{displayName}</span>
                <ChevronDown size={13} className="text-slate-400" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 z-50 mt-2 w-60 rounded-xl border border-white/10 bg-[#0A1326] p-2 shadow-2xl" data-testid="profile-menu">
                    <div className="px-2 py-1.5">
                      <div className="truncate text-sm text-white">{displayName}</div>
                      <div className="truncate text-[11px] text-slate-400">{user?.email || "Guest demo session"}</div>
                      <span className={cn("mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase", role === "admin" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/10 text-slate-300")} data-testid="header-role">
                        <Shield size={10} /> {role === "admin" ? "Admin" : "Viewer"}
                      </span>
                    </div>
                    <div className="my-1 h-px bg-white/10" />
                    <button onClick={signOut} data-testid="signout-btn" className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-200 transition-colors hover:bg-white/5 hover:text-white">
                      <LogOut size={15} /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <main key={location.pathname} className="relative z-10 mx-auto max-w-[1500px] px-4 py-6 lg:px-8 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
